/**
 * @Author: v_jppjpeng
 * @Date: 2024-03-27 01:57:32
 * @Explain: 拖拽上传
 *
 */
import { getHeaders } from '@/api/interfaceUtils';
import { Link, PageLoading, notification } from '@/components/tdesign';
import { intlGet, intlGetApiError } from '@/locales';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type {
  SuccessContext,
  TriggerContext,
  UploadChangeContext,
  UploadFile,
} from 'tdesign-react';
import { Upload } from 'tdesign-react';
// utils
import { bytesToSize } from '@/utils/format/number';

// 父组件获取拖拽过程信息
export type dropInsideInfoType = {
  /** 拖拽的文件 */
  dropFile: any[];
  /** 拖拽文件类型 */
  dropFileType: string[] | null;
  /** 鼠标在指定DOM范围 -  true: 拖拽在指定dom范围内释放鼠标  false：拖拽在已离开或不在指定dom范围内释放鼠标 */
  isInside: boolean;
  /** 鼠标在指定DOM范围内拖拽中 */
  isInsideDropIng: boolean;
  /** 上传的文件类型是否都在accept范围内 */
  uploadFileTypeAllPass: boolean;
  /** 通过点击方式选中了上传内容 */
  byClickSelectFile: any;
};

type PropsType = {
  className?: string;
  disabled?: boolean;
  disabledText?: string;
  actionApi: string;
  accept?: string;
  uploadKeyName?: any;
  /** 出现加载中的大小 */
  uploadingSize?: number;
  /** 最大上传文件大小 */
  uploadMaxSize?: number;
  uploadData?: any;
  /** 拖拽文字区域 */
  draggerRender?: any;
  loadingStyle?: CSSProperties;
  loadingText?: string;
  max?: number;
  onSuccess?: (
    data: { code: number; msg: string; data: any },
    file?: any,
  ) => void;
  onFiled?: () => void;
  onBeforeUpload?: (file: UploadFile) => boolean | Promise<boolean>;
  /** 自定义上传方法 */
  requestMethod?: (files: UploadFile | UploadFile[]) => any;
  /** 全部文件上传之前的钩子函数 */
  onBeforeAllFilesUpload?: (file: UploadFile[]) => boolean | Promise<boolean>;
  /** 父组件获取拖拽过程信息 */
  getDropInsideInfo?: (params: dropInsideInfoType) => dropInsideInfoType;
};

function DragUpload(props: PropsType, refs: any) {
  const {
    className,
    disabled,
    disabledText,
    actionApi,
    accept = 'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    uploadKeyName,
    uploadingSize = 0,
    uploadMaxSize = 0,
    uploadData,
    draggerRender,
    loadingStyle,
    loadingText = intlGet('COMMON.UPLOAD.FILE.BIG.WAIT'),
    onSuccess,
    onFiled,
    onBeforeUpload,
    requestMethod,
    onBeforeAllFilesUpload,
    getDropInsideInfo,
  } = props;
  const uploadDom = useRef<any>();
  // 上传文件指定DOM
  const dragContentDomRef = useRef<HTMLDivElement>(null);

  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState(0);
  const [fileList, setFileList] = useState<any[]>([]);
  // 拖拽的文件
  const [dropFile, setDropFile] = useState<any[]>([]);
  // 拖拽文件类型
  const [dropFileType, setDropFileType] = useState<string[] | null>(null);
  // 鼠标在指定DOM范围 -  true: 拖拽在指定dom范围内释放鼠标  false：拖拽在已离开或不在指定dom范围内释放鼠标
  const [isInside, setIsInside] = useState<boolean>(false);
  // 鼠标在指定DOM范围内拖拽中
  const [isInsideDropIng, setIsInsideDropIng] = useState<boolean>(false);
  // 上传的文件类型是否都在accept范围内
  const [uploadFileTypeAllPass, setUploadFileTypeAllPass] =
    useState<boolean>(false);
  // 是否在拖拽区域内发起点击唤起获取文件弹窗
  const [dropBoxClick, setDropBoxClick] = useState<boolean>(false);
  // 选择的文件
  const [selectFile, setSelectFile] = useState<any>(null);

  // 自定义拖拽区域
  function renderCustomDrag({ dragActive }: TriggerContext) {
    if (disabled) {
      return <p>{disabledText}</p>;
    }
    if (dragActive) {
      return <p>{intlGet('COMMON.RELEASE.MOUSE')}</p>;
    }
    return progress < 1 ? (
      <div className="content-text">
        <Link>{intlGet('COMMON.CLICK.UPLOAD')}</Link>
        <span>/{intlGet('COMMON.DRAG.AREA')}</span>
      </div>
    ) : null;
  }

  const onClear = () => {
    setFileList([]);
    setIsPageLoading(false);
  };

  // 上传前
  const beforeUpload = async (file: UploadFile) => {
    let isAccepted = true;
    const fileSize = file?.size || 0;
    if (disabled) return false;

    setIsPageLoading(true);

    // 上传文件前调用 - 自定义处理上传前拦截提示信息、是否继续上传
    if (onBeforeUpload) {
      isAccepted = await onBeforeUpload(file);
      // 自定义处理上传前拦截不继续上传，关闭当前上传状态
      if (!isAccepted) {
        onClear();
      }
      return isAccepted;
    }

    // 判断是否超过限制文件大小
    if (fileSize > uploadMaxSize) {
      isAccepted = false;
      notification.error({
        description: intlGet('COMMON.UPLOAD.ERROR.EXCEED', {
          maxSize: bytesToSize(uploadMaxSize),
        }),
      });
    }
    return isAccepted;
  };

  // 开始上传
  const handleChange = (file: any[], context: UploadChangeContext) => {
    setFileList(file);
  };

  // 上传成功
  const handleSuccess = ({ response, file }: SuccessContext) => {
    // 普通上传 - 不经过接口上传
    if (!actionApi) {
      onSuccess?.(response, file);
    } else {
      const { code, msg } = response;
      if (code === 0) {
        notification.success({
          description: intlGet('COMMON.UPLOAD.SUCCEEDED'),
        });
        onSuccess?.(response, file);
      } else {
        onFiled && onFiled?.();
        notification.error({
          description:
            intlGetApiError(code) || intlGet('COMMON.UPLOAD.ERROR.TRY.AGAIN'),
        });
      }
    }
    onClear();
  };

  // 匹配上传文件类型是否符合传入accept要求
  const dropFileTypeCheck = (uploadTypeArr: string[] | null) => {
    const acceptArr = accept.split(',');
    if (uploadTypeArr) {
      const res = uploadTypeArr.every((i: string) => acceptArr.includes(i));
      setUploadFileTypeAllPass(res);
      return;
    }
    setUploadFileTypeAllPass(false);
  };

  // 获取拖拽是鼠标是否在指定DOM范围内信息
  const getDropInfo = () => {
    // 获取指定拖拽DOM范围
    const dragContentDom = dragContentDomRef?.current;
    if (dragContentDom) {
      // 在指定范围内拖拽时
      document.addEventListener('click', (e: any) => {
        if (dragContentDom.contains(e.target)) {
          setDropBoxClick(true);
        } else {
          setDropBoxClick(false);
        }
      });
      // 当被拖动的对象在目标容器范围内拖动时触发此事件
      document.addEventListener('dragover', (e: any) => {
        setDropBoxClick(false);
        if (dragContentDom.contains(e.target)) {
          setIsInsideDropIng(true);
        } else {
          setIsInsideDropIng(false);
        }
      });
      // 当被鼠标拖动的对象离开目标容器时触发此事件
      dragContentDom.addEventListener('dragleave', (e: any) => {
        setIsInside(false);
        setIsInsideDropIng(false);
      });
      // 在一个拖动过程中，释放鼠标键到目标容器时触发此事件
      document.addEventListener('drop', (e: any) => {
        setDropBoxClick(false);
        // 上传的文件type
        let uploadTypeArr: string[] | null = null;
        if (dragContentDom.contains(e.target)) {
          setIsInside(true);
          setIsInsideDropIng(false);
          const { files } = e.dataTransfer;

          // 获取文件名称后缀
          if (files?.length)
            for (const i in files) {
              const type = files[i]?.type;
              if (type && !uploadTypeArr) {
                uploadTypeArr = [type];
              } else if (type && uploadTypeArr?.length) {
                uploadTypeArr.push(type);
              }
            }
          setDropFile(files);
          setDropFileType(uploadTypeArr);
          dropFileTypeCheck(uploadTypeArr);
        } else {
          setIsInside(false);
          setDropFileType(uploadTypeArr);
          dropFileTypeCheck(uploadTypeArr);
        }
      });
    }
  };

  // 阻止浏览器默认行为 - 避免拖拽非指定区域唤起浏览器下载
  const preventDefault = () => {
    // 阻止默认行为，防止浏览器下载文件
    const handleDragOver = (event: any) => event.preventDefault();
    // 阻止默认行为
    const handleDrop = (event: any) => event.preventDefault();
    // 添加事件监听器
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);
  };

  useEffect(() => {
    // 执行监听
    preventDefault();
    getDropInfo();
    // 清理函数，在组件卸载时移除事件监听器
    return () => {
      preventDefault();
      getDropInfo();
    };
  }, []);

  useEffect(() => {
    if (getDropInsideInfo && !disabled)
      getDropInsideInfo({
        isInside,
        dropFile,
        dropFileType,
        isInsideDropIng,
        uploadFileTypeAllPass,
        byClickSelectFile:
          selectFile?.length && dropBoxClick ? selectFile : null,
      });
  }, [
    isInside,
    dropFile,
    dropFileType,
    isInsideDropIng,
    uploadFileTypeAllPass,
    selectFile,
    dropBoxClick,
  ]);

  // 重置父组件获取拖拽过程信息
  const onResetDropInfo = () => {
    setDropFile([]);
    setDropFileType(null);
    setIsInside(false);
    setIsInsideDropIng(false);
    setUploadFileTypeAllPass(false);
    setSelectFile([]);
  };

  // 绑定ref，抛出方法
  useImperativeHandle(refs, () => ({
    onResetDropInfo,
  }));

  return (
    <>
      <PageLoading
        absolute
        size="small"
        isShow={isPageLoading}
        style={loadingStyle}
        loadingText={loadingText}
      />
      <Upload
        ref={uploadDom}
        disabled={disabled}
        className={classNames('default-td-drag-upload', className)}
        draggable={true}
        theme={'custom'}
        headers={getHeaders() as any}
        action={actionApi}
        name={uploadKeyName}
        accept={accept}
        data={uploadData}
        files={fileList}
        dragContent={(config) => (
          <div id="is-drag-content" ref={dragContentDomRef}>
            {/* 上传文件 - 相关描述 */}
            {draggerRender ? draggerRender?.(config) : renderCustomDrag(config)}
          </div>
        )}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        // onFail={handleFail}
        onSuccess={handleSuccess}
        onProgress={(val) => setProgress(+val)}
        requestMethod={requestMethod}
        beforeAllFilesUpload={onBeforeAllFilesUpload}
        onSelectChange={(file) => setSelectFile(file)}
      />
    </>
  );
}

export default forwardRef(DragUpload);
